import React, { useState } from 'react';
import PropTypes from 'prop-types';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import AppProductProp from '../../../profile/propTypes/appProductProp';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../../../profile/propTypes/StripeCardProp';
import {
  CHALLENGE_TAB,
  GAME_TYPE,
  IN_APP_PRODUCT_TYPE,
  isItemFree,
  JOURNEY_STEP_TYPE,
  snakeToCamel,
} from '../../../../services/utilityFunctions';
import ChallengeDetails from '../../../challenges/ChallengeDetails';
import Notifications from '../../../../services/notifications';
import translate from '../../../../commonComponents/translate';
import JourneyContentCourse from './JourneyContentCourse';
import Profile from '../../../profile/Profile';
import HardSkillProp from '../../../challenges/propTypes/hardSkillProp';
import TrophyProp from '../../propTypes/trophyProp';
import PerformanceProp from '../../../profile/propTypes/performanceProp';
import ChartsProp from '../../../profile/propTypes/chartsProp';
import CertificateProp from '../../../academy/propTypes/certificateProp';
import JourneyContentSurvey from './JourneyContentSurvey';
import JourneyContentTask from './JourneyContentTask';
import CertificateDisplay from '../../../academy/details/CertificateDisplay';
import JourneyProp from '../../propTypes/journeyProp';
import PrintModal from '../../../profile/mainPanel/print/PrintModal';
import EnrollModal from '../../../academy/EnrollModal';
import VatTypesProp from '../../../../commonComponents/forms/Address/VatTypesProp';
import CurrencyProp from '../../../profile/propTypes/currencyProp';
import OrganizationProp from '../../../../commonComponents/propTypes/organizationProp';
import JourneyContentSignaturePdf from './JourneyContentSignaturePdf';
import JourneyContentPurchase from './JourneyContentPurchase';
import JourneyContentSession from './JourneyContentSession';
import JourneyUploadDocument from './JourneyUploadDocument';

const activeChallengeTab = challenge => {
  const challengeType = challenge.gameType
    ? challenge.gameType.type
    : GAME_TYPE.PRODUCTIVITY;

  switch (challengeType) {
    case GAME_TYPE.PRODUCTIVITY:
      return CHALLENGE_TAB.LEADERBOARD;
    case GAME_TYPE.CROSS_SELLING:
      return CHALLENGE_TAB.LEADS;
    case GAME_TYPE.CANDIDATES:
      return CHALLENGE_TAB.RECOMMENDATIONS;
    case GAME_TYPE.E_LEARNING:
      return CHALLENGE_TAB.COURSE;
    case GAME_TYPE.PROFILING:
    default:
      return CHALLENGE_TAB.TIMELINE;
  }
};

const activeProfileTab = ({ type }) => snakeToCamel(type.slice(8));

function JourneyContent(props) {
  const {
    t,
    step,
    onChange,
    countries,
    cards,
    languages,
    experienceDomainSkills,
    hardSkills,
    stats,
    customTrophies,
    certificate,
    appProducts,
    journey,
    pspdfkitLicenseKey,
    vatTypes,
    allLanguages,
    currencies,
    userLanguage,
    directSellingActive,
    sessionContext,
    journeyContext,
    organization,
    sessionVerifiedParticipation,
    onSkipStep,
  } = props;

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  function EmptyStep() {
    return (
      <div className="m-portlet m-portlet--rounded">
        <div className="m-portlet__body course-container-body">
          <div className="journey-empty-step">
            <div className="journey-empty-step-text">{t('journey_noStep')}</div>
          </div>
        </div>
      </div>
    );
  }

  function Content() {
    switch (step.type) {
      case JOURNEY_STEP_TYPE.GAME: {
        if (!step.game) {
          Notifications.showNotificationError(
            t('error'),
            t('error_fetchStepGame'),
          );
          return <EmptyStep />;
        }
        return (
          <ChallengeDetails
            activeTab={activeChallengeTab(step.game)}
            challenge={step.game}
            appProducts={appProducts}
            countries={countries}
            cards={cards}
            onChange={onChange}
            pspdfkitLicenseKey={pspdfkitLicenseKey}
            currencies={currencies}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      }
      case JOURNEY_STEP_TYPE.COURSE: {
        if (!step.course) {
          Notifications.showNotificationError(
            t('error'),
            t('error_fetchStepCourse'),
          );
          return <EmptyStep />;
        }
        return (
          <JourneyContentCourse
            journeyId={journey._id}
            step={step}
            appProducts={appProducts}
            countries={countries}
            cards={cards}
            onChange={onChange}
            pspdfkitLicenseKey={pspdfkitLicenseKey}
            sessionContext={sessionContext}
            journeyContext={journeyContext}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      }
      case JOURNEY_STEP_TYPE.SURVEY: {
        if (!step.survey) {
          Notifications.showNotificationError(
            t('error'),
            t('error_fetchStepSurvey'),
          );
          return <EmptyStep />;
        }
        return (
          <JourneyContentSurvey
            survey={step.survey}
            contextId={step.surveyContext}
            onChange={onChange}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      }
      case JOURNEY_STEP_TYPE.CERTIFICATE: {
        if (step.type !== JOURNEY_STEP_TYPE.CERTIFICATE) {
          Notifications.showNotificationError(
            t('error'),
            t('error_fetchStepCertificate'),
          );
          return <EmptyStep />;
        }
        return (
          <CertificateDisplay
            certificate={certificate}
            journey={journey}
            typeCertificate="journey"
            appProducts={appProducts}
            journeyContext={journeyContext}
            sessionContext={sessionContext}
            onPrint={() => setShowPrintModal(true)}
            onEmail={() => setShowEnrollModal(true)}
          />
        );
      }
      case JOURNEY_STEP_TYPE.TASK:
        return (
          <JourneyContentTask
            step={step}
            onChange={onChange}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      case JOURNEY_STEP_TYPE.SIGNATURE:
        return (
          <JourneyContentSignaturePdf
            step={step}
            onRefreshJourney={onChange}
            pspdfkitLicenseKey={pspdfkitLicenseKey}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      case JOURNEY_STEP_TYPE.FILL_AND_SIGN:
        return (
          <JourneyContentSignaturePdf
            step={step}
            onRefreshJourney={onChange}
            pspdfkitLicenseKey={pspdfkitLicenseKey}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
            isFillAndSign
          />
        );
      case JOURNEY_STEP_TYPE.DOCUMENT:
        return (
          <JourneyUploadDocument
            step={step}
            onRefreshJourney={onChange}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      case JOURNEY_STEP_TYPE.MARKET_PRODUCT_PURCHASE:
        return (
          <JourneyContentPurchase
            step={step}
            slug={journey.slug}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      case JOURNEY_STEP_TYPE.SESSION:
        return (
          <JourneyContentSession
            session={step?.session || {}}
            languages={languages}
            journeyId={journey._id}
            journeySlug={journey.slug}
            // Paid journeys cover their sessions with a single payment, so the
            // per-session Enroll button is hidden; free journeys let the user
            // buy a paid session (incl. tickets for others) on the session page.
            journeyIsFree={isItemFree(journey)}
            sessionVerifiedParticipation={sessionVerifiedParticipation}
            onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
          />
        );
      default: {
        if (step.type && step.type.startsWith('profile')) {
          return (
            <Profile
              hideSidePanel
              cards={cards}
              countries={countries}
              activeTab={activeProfileTab(step)}
              languages={languages}
              experienceDomainSkills={experienceDomainSkills}
              hardSkills={hardSkills}
              customTrophies={customTrophies}
              stats={stats}
              certificates={[]}
              appProducts={appProducts}
              onChange={onChange}
              vatTypes={vatTypes}
              allLanguages={allLanguages}
              currencies={currencies}
              userLanguage={userLanguage}
              directSellingActive={directSellingActive}
              organization={organization}
              isInJourney
              shouldRefreshData={
                step.type === JOURNEY_STEP_TYPE.PROFILE_DOCUMENTS
              }
              onSkipStep={step?.isOptional ? () => onSkipStep(step?._id) : null}
            />
          );
        }
        return <EmptyStep />;
      }
    }
  }

  return (
    <div className="row">
      <div className="col-12 journeyContent-wrapper">
        {showPrintModal && (
          <PrintModal
            certificateId={certificate._id}
            cards={cards}
            countries={countries}
            appProduct={appProducts.find(
              p => p.productType === IN_APP_PRODUCT_TYPE.CERTIFICATE,
            )}
            product
            onClose={() => setShowPrintModal(false)}
          />
        )}
        {showEnrollModal && (
          <EnrollModal
            itemType={IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE}
            itemId={certificate._id}
            onClose={() => setShowEnrollModal(false)}
          />
        )}
        <Content />
      </div>
    </div>
  );
}

const compareStep = (prevProps, nextProps) => {
  if (!prevProps.step || !nextProps.step) return false;

  const sameId = prevProps.step._id === nextProps.step._id;
  const sameStatus = prevProps.step.status === nextProps.step.status;
  const sameTaskStatus =
    prevProps.step.taskStatus === nextProps.step.taskStatus;

  // List of step types that should be treated as "tasks"
  const taskStepTypes = [
    JOURNEY_STEP_TYPE.TASK,
    JOURNEY_STEP_TYPE.SIGNATURE,
    JOURNEY_STEP_TYPE.FILL_AND_SIGN,
  ];

  const isTask =
    taskStepTypes.includes(prevProps.step.type) &&
    taskStepTypes.includes(nextProps.step.type);

  if (isTask) {
    return sameId && sameStatus && sameTaskStatus;
  }

  return sameId;
};

JourneyContent.propTypes = {
  t: PropTypes.func.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  onChange: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  experienceDomainSkills: PropTypes.arrayOf(PropTypes.string).isRequired,
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  customTrophies: PropTypes.arrayOf(PropTypes.shape(TrophyProp.propType))
    .isRequired,
  stats: PropTypes.shape({
    performance: PropTypes.shape(PerformanceProp.propType),
    charts: PropTypes.shape(ChartsProp.propType),
  }).isRequired,
  certificate: PropTypes.shape(CertificateProp.propType),
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  journey: PropTypes.shape(JourneyProp.propType).isRequired,
  pspdfkitLicenseKey: PropTypes.string,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  allLanguages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  userLanguage: PropTypes.string.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  sessionContext: PropTypes.string,
  journeyContext: PropTypes.string,
  organization: PropTypes.shape(OrganizationProp.propType).isRequired,
  sessionVerifiedParticipation: PropTypes.bool,
  onSkipStep: PropTypes.func.isRequired,
};

JourneyContent.defaultProps = {
  certificate: null,
  pspdfkitLicenseKey: null,
  sessionContext: null,
  journeyContext: null,
  sessionVerifiedParticipation: false,
};

export default React.memo(translate(JourneyContent), compareStep);
